import { Edit, CheckCircle, AlertTriangle } from "lucide-react";
import { Button } from "./ui/Button";
import { Card, CardContent } from "./ui/Card";
import { Badge } from "./ui/Badge";
import type { CheckoutParticipant } from "../lib/checkout-participant";
import { getParticipantKey } from "../lib/checkout-participant";

interface ParticipantListProps {
  participants: CheckoutParticipant[];
  onEditParticipant: (participant: CheckoutParticipant) => void;
}

export const ParticipantList = ({
  participants,
  onEditParticipant,
}: ParticipantListProps) => {
  if (participants.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <p className="text-gray-500">No participants found</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {participants.map((participant) => {
        return (
          <Card
            key={getParticipantKey(participant)}
            variant={participant.isComplete ? "default" : "outlined"}
            className={
              participant.isComplete ? "" : "border-orange-300 bg-orange-50/30"
            }
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1">
                  {participant.isComplete ? (
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 mb-2">
                      {participant.displayName}
                    </h3>
                    {participant.hasExpiredConsents && (
                      <Badge variant="warning" size="sm" className="w-fit">
                        Consent review required
                      </Badge>
                    )}
                    {!participant.isComplete && !participant.hasExpiredConsents && (
                      <Badge variant="warning" size="sm">
                        {participant.missingConsents.length} form
                        {participant.missingConsents.length !== 1
                          ? "s"
                          : ""}{" "}
                        incomplete
                      </Badge>
                    )}
                    {participant.isComplete && (
                      <Badge variant="success" size="sm">
                        All forms complete
                      </Badge>
                    )}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEditParticipant(participant)}
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Edit
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
